import ColorPicker from "components/basic/color-picker/ColorPicker";
import ProductTemplateColumnsDropdown from "components/basic/dropdowns/product-template-columns-dropdown/ProductTemplateColumnsDropdown";

import TimeSpanInputs from "components/fragments/product-template-configurator/ProductTemplateAttributesConfiguration/type-fields/components/TimeSpanInputs";
import {
  IProductTemplateDropdownChangeEvent,
  ProductTemplateDropdown,
} from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import { DashboardEditorProps } from "components/ComponentTypeTypes";
import { Divider } from "primereact/divider";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import React, { ChangeEvent, useEffect, useState } from "react";
import { T } from "services/lang/LanguageService";
import { DropdownChangeEvent } from "primereact/dropdown";
import { MultiSelectChangeEvent } from "primereact/multiselect";
import { allowedSubtypes, allowedTypes, ITiledProductItemViewEditor } from "../TiledProductItemViewTypes";

const TiledProductItemViewEditor: React.FC<DashboardEditorProps<ITiledProductItemViewEditor>> = ({
  config,
  onConfigChange,
}) => {
  const [currentConfig, setCurrentConfig] = useState<ITiledProductItemViewEditor>(config);

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const safeNameSelectionHandler = (e: IProductTemplateDropdownChangeEvent) => {
    setCurrentConfig(prevState => ({ ...prevState, safeName: e.refName, id: e.value }));
  };

  const handleCurrentConfigChange = (e: MultiSelectChangeEvent | DropdownChangeEvent | ChangeEvent<HTMLInputElement>) => {
    setCurrentConfig(prevState => ({ ...prevState, [e.target.id]: e.target.value }));
  };

  return (
    <div className="flex flex-column gap-2">
      <div className="flex flex-row gap-2 justify-content-between">
        <div className="flex flex-row gap-2">
          <div className="w-15rem flex flex-column gap-2">
            <label htmlFor="safeName">{T("label.product-template-table")}</label>
            <ProductTemplateDropdown value={currentConfig.id} onChange={safeNameSelectionHandler} />
          </div>
          <div className="w-15rem flex flex-column gap-2">
            <label htmlFor="filter">{T("word.filter")}</label>
            <InputText id="filter" value={currentConfig?.filter ?? ""} onChange={handleCurrentConfigChange} />
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <div className="flex flex-wrap md:flex-nowrap gap-3 mb-3">
            <div className="gap-2 flex flex-column justify-content-center">
              <label>{`${T("word.datasets")} ${T("word.max")} ${T("word.age").toLowerCase()}`}</label>
              <div className="p-inputgroup">
                <TimeSpanInputs
                  value={currentConfig.maxAge ?? 0}
                  onChange={val => setCurrentConfig({ ...currentConfig, maxAge: val })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {currentConfig.safeName && (
        <div className="flex flex-column gap-2">
          <Divider />
          <div className="flex flex-row gap-2">
            <div className="w-15rem flex flex-column gap-2">
              <label htmlFor="valueColumn">{T("word.value")}</label>
              <ProductTemplateColumnsDropdown
                id="valueColumn"
                value={currentConfig.valueColumn ? [currentConfig.valueColumn] : []}
                className="w-full"
                onChange={handleCurrentConfigChange}
                selectedTemplate={{
                  refName: currentConfig.safeName,
                  value: currentConfig.id!,
                }}
                singleSelect
                allowedTypes={allowedTypes}
              />
            </div>
            <div className="w-15rem flex flex-column gap-2">
              <label htmlFor="setpointColumn">{T("config.setpoint-column")}</label>
              <ProductTemplateColumnsDropdown
                id="setpointColumn"
                value={currentConfig.setpointColumn ? [currentConfig.setpointColumn] : []}
                className="w-full"
                onChange={handleCurrentConfigChange}
                selectedTemplate={{
                  refName: currentConfig.safeName,
                  value: currentConfig.id!,
                }}
                singleSelect
                allowedTypes={allowedTypes}
                allowedSubtypes={allowedSubtypes}
              />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="w-15rem flex flex-column gap-2">
              <label htmlFor="columnsToDisplayInTooltip">{T("label.columns-to-display-in-tooltip")}</label>
              <ProductTemplateColumnsDropdown
                id="columnsToDisplayInTooltip"
                value={currentConfig.columnsToDisplayInTooltip ? currentConfig.columnsToDisplayInTooltip : []}
                className="w-full"
                onChange={handleCurrentConfigChange}
                selectedTemplate={{
                  refName: currentConfig.safeName,
                  value: currentConfig.id!,
                }}
              />
            </div>
            <div className="w-15rem flex flex-column gap-2">
              <label htmlFor="decimalPlaces">{T("config.decimal-places")}</label>
              <InputNumber
                id="decimalPlaces"
                value={currentConfig.decimalPlaces}
                onChange={e =>
                  setCurrentConfig(prevState => ({
                    ...prevState,
                    decimalPlaces: e.value === null ? undefined : e.value,
                  }))
                }
              />
            </div>
            <div className="w-15rem flex flex-column gap-2">
              <label htmlFor="defaultColor">{T("label.default-color")}</label>
              <ColorPicker id="defaultColor" value={currentConfig.defaultColor} onValueChange={handleCurrentConfigChange} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiledProductItemViewEditor;
